import React, { useState, useEffect, useRef } from 'react';
import JSZip from 'jszip';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import './App.css';

const ROLES = ['Author', 'Contributor', 'Editor', 'Administrator'];

function App() {
  const [staffMembers, setStaffMembers] = useState([]);
  const [currentMember, setCurrentMember] = useState({
    name: '',
    email: '',
    bio: '',
    role: 'Contributor',
    pageContent: '',
    x: '',
    facebook: '',
    threads: '',
    tiktok: '',
    mastodon: '',
    linkedin: '',
    bluesky: '',
    youtube: '',
    instagram: '',
    photo: null,
    photoPreview: null
  });
  const [errors, setErrors] = useState({});
  const quillRef = useRef(null);
  const quillInstanceRef = useRef(null);
  const quillInitializedRef = useRef(false);
  
  // Read feature flags from environment variables
  const BRANDING = process.env.REACT_APP_BRANDING === 'true';
  const PAGE_CREATION = process.env.REACT_APP_PAGE_CREATION === 'true';

  // Initialize Quill editor
  useEffect(() => {
    if (!PAGE_CREATION) {
      // Clean up if PAGE_CREATION is disabled
      if (quillInstanceRef.current) {
        quillInstanceRef.current = null;
        quillInitializedRef.current = false;
      }
      if (quillRef.current) {
        quillRef.current.innerHTML = '';
      }
      return;
    }

    if (!quillRef.current) {
      return;
    }

    const container = quillRef.current;
    
    // Only initialize if we don't have an instance yet
    if (quillInstanceRef.current) {
      return;
    }
    
    // If container already has Quill structure, clear it first
    // This ensures we start fresh
    if (container.querySelector('.ql-container') || container.querySelector('.ql-toolbar')) {
      container.innerHTML = '';
    }

    // Mark as initializing to prevent double init
    quillInitializedRef.current = true;
    
    const quill = new Quill(container, {
      theme: 'snow',
      placeholder: 'Enter content for the staff profile page...',
      modules: {
        toolbar: [
          [{ 'header': [false, 2, 3] }],
          ['bold', 'italic', 'underline'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          ['link']
        ]
      }
    });

    // Handle text changes
    quill.on('text-change', () => {
      const html = quill.getSemanticHTML();
      // Only update if content actually changed (not just empty p tag)
      if (html && html !== '<p><br></p>' && html.trim() !== '') {
        setCurrentMember(prev => ({ ...prev, pageContent: html }));
      } else {
        setCurrentMember(prev => ({ ...prev, pageContent: '' }));
      }
    });

    quillInstanceRef.current = quill;

    return () => {
      // Cleanup: destroy Quill instance
      if (quillInstanceRef.current) {
        try {
          quillInstanceRef.current.off('text-change');
        } catch (e) {
          // Ignore errors
        }
        quillInstanceRef.current = null;
      }
      quillInitializedRef.current = false;
    };
  }, [PAGE_CREATION]);

  // Clear Quill editor when form is reset (pageContent becomes empty)
  useEffect(() => {
    if (quillInstanceRef.current && currentMember.pageContent === '') {
      // Clear the editor content
      quillInstanceRef.current.setContents([], 'silent');
    }
  }, [currentMember.pageContent]);

  // Generate email from name
  const generateEmail = (name) => {
    if (!name.trim()) return '';
    const cleanName = name.trim()
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove punctuation and special characters
      .replace(/\s+/g, '');         
    return `${cleanName}@noreply.invalid`;
  };

  // Generate slug from name
  const generateSlug = (name) => {
    if (!name.trim()) return '';
    return name.trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Generate a random ID (24 hex characters, like MongoDB ObjectId format)
  const generateId = () => {
    return Array.from({ length: 24 }, () => 
      Math.floor(Math.random() * 16).toString(16)
    ).join('');
  };

  // Generate a UUID v4 format
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.floor(Math.random() * 16);
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  };

  // Handle name change and auto-generate email
  const handleNameChange = (e) => {
    const name = e.target.value;
    const email = generateEmail(name);
    setCurrentMember(prev => ({
      ...prev,
      name,
      email: email || prev.email // Only update if name generates an email
    }));
  };

  // Handle bio change with real-time validation
  const handleBioChange = (e) => {
    const bio = e.target.value;
    setCurrentMember(prev => ({ ...prev, bio }));
    
    // Clear bio error if within limit
    if (bio.length <= 250 && errors.bio) {
      setErrors(prev => ({ ...prev, bio: undefined }));
    }
  };

  // Handle photo upload
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, photo: 'Please select an image file' }));
        return;
      }
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setCurrentMember(prev => ({
          ...prev,
          photo: file,
          photoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);
      
      // Clear photo error
      if (errors.photo) {
        setErrors(prev => ({ ...prev, photo: undefined }));
      }
    }
  };

  // Remove photo
  const handleRemovePhoto = () => {
    setCurrentMember(prev => ({
      ...prev,
      photo: null,
      photoPreview: null
    }));
    // Reset file input
    const fileInput = document.getElementById('photo');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!currentMember.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!currentMember.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentMember.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (currentMember.bio.length > 250) {
      newErrors.bio = 'Bio must be 250 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add staff member
  const addStaffMember = () => {
    if (validateForm()) {
      const newMember = {
        name: currentMember.name.trim(),
        email: currentMember.email.trim(),
        bio: currentMember.bio.trim(),
        pageContent: currentMember.pageContent,
        roles: [currentMember.role],
        twitter: currentMember.x.trim(),
        facebook: currentMember.facebook.trim(),
        threads: currentMember.threads.trim(),
        tiktok: currentMember.tiktok.trim(),
        mastodon: currentMember.mastodon.trim(),
        linkedin: currentMember.linkedin.trim(),
        bluesky: currentMember.bluesky.trim(),
        youtube: currentMember.youtube.trim(),
        instagram: currentMember.instagram.trim(),
        photo: currentMember.photo,
        photoPreview: currentMember.photoPreview
      };
      
      setStaffMembers(prev => [...prev, newMember]);
      setCurrentMember({
        name: '',
        email: '',
        bio: '',
        role: 'Contributor',
        pageContent: '',
        x: '',
        facebook: '',
        threads: '',
        tiktok: '',
        mastodon: '',
        linkedin: '',
        bluesky: '',
        youtube: '',
        instagram: '',
        photo: null,
        photoPreview: null
      });
      setErrors({});
      // Reset file input
      const fileInput = document.getElementById('photo');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  // Remove staff member
  const removeStaffMember = (index) => {
    setStaffMembers(prev => prev.filter((_, i) => i !== index));
  };

  // Export JSON and photos as ZIP
  const exportJSON = async () => {
    const now = new Date().toISOString();
    const timestamp = Date.now();
    
    // Generate user IDs first so we can reference them
    // Also generate photo filenames for profile_image paths
    const usersWithIds = staffMembers.map((member, index) => {
      let profileImage = null;
      if (member.photo) {
        const originalName = member.photo.name;
        const extension = originalName.substring(originalName.lastIndexOf('.'));
        const filename = `${generateSlug(member.name)}${extension}`;
        profileImage = `/images/builder-uploads/${filename}`;
      }
      
      return {
        id: generateId(),
        name: member.name,
        email: member.email,
        bio: member.bio || null,
        roles: member.roles,
        profile_image: profileImage,
        twitter: member.twitter || null,
        facebook: member.facebook || null,
        threads: member.threads || null,
        tiktok: member.tiktok || null,
        mastodon: member.mastodon || null,
        linkedin: member.linkedin || null,
        bluesky: member.bluesky || null,
        youtube: member.youtube || null,
        instagram: member.instagram || null
      };
    });

    const data = {
      users: usersWithIds
    };

    // Add pages, tags, and linking tables if PAGE_CREATION is enabled
    if (PAGE_CREATION && staffMembers.length > 0) {
      // Generate tag ID
      const tagId = generateId();
      
      // Create the staff-profile tag
      data.tags = [{
        id: tagId,
        name: "#staff-profile",
        slug: "hash-staff-profile",
        visibility: "internal"
      }];

      // Create posts (pages) and linking tables
      const posts = [];
      const postsAuthors = [];
      const postsTags = [];
      let sortOrder = 0;

      usersWithIds.forEach((user, index) => {
        const originalMember = staffMembers[index];
        
        // Only create a page if pageContent is provided and not empty
        const pageContent = originalMember.pageContent;
        if (!pageContent || pageContent.trim() === '' || pageContent === '<p><br></p>') {
          return; // Skip creating a page for this user
        }
        
        const postId = generateId();
        const postAuthorId = generateId();
        const postTagId = generateId();
        const uuid = generateUUID();
        
        const html = pageContent;

        posts.push({
          id: postId,
          uuid: uuid,
          title: user.name,
          slug: generateSlug(user.name),
          mobiledoc: null,
          html: html,
          comment_id: postId,
          feature_image: null,
          featured: 0,
          type: "page",
          status: "published",
          visibility: "public",
          email_recipient_filter: "all",
          created_at: now,
          updated_at: now,
          published_at: now,
          custom_excerpt: null,
          codeinjection_head: null,
          codeinjection_foot: null,
          custom_template: null,
          canonical_url: null,
          newsletter_id: null,
          show_title_and_feature_image: 1
        });

        // Link post to author using the user's ID
        postsAuthors.push({
          id: postAuthorId,
          post_id: postId,
          author_id: user.id,
          sort_order: sortOrder
        });

        // Link post to tag
        postsTags.push({
          id: postTagId,
          post_id: postId,
          tag_id: tagId,
          sort_order: 0
        });

        sortOrder++;
      });

      data.posts = posts;
      data.posts_authors = postsAuthors;
      data.posts_tags = postsTags;
    }

    const exportData = {
      db: [{
        meta: {
          exported_on: timestamp,
          version: "5.0.0"
        },
        data: data
      }]
    };

    const dataStr = JSON.stringify(exportData, null, 2);

    // Create ZIP file
    const zip = new JSZip();
    
    // Add JSON file to zip
    zip.file('ghost-staff-import.json', dataStr);

    // Add photos to images/builder-uploads folder
    const hasPhotos = staffMembers.some(member => member.photo);
    if (hasPhotos) {
      const imagesFolder = zip.folder('images');
      const builderUploadsFolder = imagesFolder.folder('builder-uploads');

      // Add each photo to the zip
      for (let i = 0; i < staffMembers.length; i++) {
        const member = staffMembers[i];
        if (member.photo) {
          // Generate a filename from the member's name (must match profile_image path)
          const originalName = member.photo.name;
          const extension = originalName.substring(originalName.lastIndexOf('.'));
          const filename = `${generateSlug(member.name)}${extension}`;
          
          // Read the file and add it to the zip
          const fileData = await member.photo.arrayBuffer();
          builderUploadsFolder.file(filename, fileData);
        }
      }
    }

    // Generate zip file and download
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ghost-staff-import.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="app">
      <div className="container">
        <header className="header">
          <h1>Ghost Staff Writer</h1>
          {BRANDING && (
            <h2>A service from <a href="https://www.spectralwebservices.com">Spectral Web Services</a></h2>
          )}
          <p>Create a .zip file you can import to Ghost to add new staff members in bulk</p>
        </header>

        <div className="form-section">
          <h2>Add Staff Member</h2>
          <div className="form">
            <div className="form-group">
              <label htmlFor="name">Name *</label>
              <input
                type="text"
                id="name"
                value={currentMember.name}
                onChange={handleNameChange}
                className={errors.name ? 'error' : ''}
                placeholder="Enter full name"
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                value={currentMember.email}
                onChange={(e) => setCurrentMember(prev => ({ ...prev, email: e.target.value }))}
                className={errors.email ? 'error' : ''}
                placeholder="email@example.com"
              />
              <div className="help-text">
                💡 You can customize this to use a real email address instead of the auto-generated one
              </div>
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                value={currentMember.role}
                onChange={(e) => setCurrentMember(prev => ({ ...prev, role: e.target.value }))}
              >
                {ROLES.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="bio">Bio</label>
              <textarea
                id="bio"
                value={currentMember.bio}
                onChange={handleBioChange}
                className={currentMember.bio.length > 250 ? 'error' : ''}
                placeholder="Brief bio (max 250 characters)"
                rows="3"
              />
              <div className="char-count">
                <span className={currentMember.bio.length > 250 ? 'char-count-error' : ''}>
                  {currentMember.bio.length}/250 characters
                </span>
              </div>
              {currentMember.bio.length > 250 && (
                <span className="error-message">Bio must be 250 characters or less</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="photo">Photo (Optional)</label>
              <input
                type="file"
                id="photo"
                accept="image/*"
                onChange={handlePhotoChange}
                className={errors.photo ? 'error' : ''}
              />
              {errors.photo && <span className="error-message">{errors.photo}</span>}
              {currentMember.photoPreview && (
                <div className="photo-preview">
                  <img src={currentMember.photoPreview} alt="Preview" />
                  <button 
                    type="button" 
                    onClick={handleRemovePhoto}
                    className="remove-photo-button"
                  >
                    Remove Photo
                  </button>
                </div>
              )}
            </div>

            {PAGE_CREATION && (
              <div className="form-group">
                <label htmlFor="pageContent">Page Content (Optional)</label>
                <div className="quill-wrapper">
                  <div ref={quillRef} key="quill-editor" />
                </div>
                <div className="help-text">
                  💡 A page will be automatically created for this staff member if page content is added. If your theme doesn't support this, just leave it blank.
                </div>
              </div>
            )}

            {/* Social Media Fields */}
            <div className="social-section">
              <h3>Social Media (Optional)</h3>
              <div className="social-grid">
                <div className="form-group">
                  <label htmlFor="x">X (Twitter)</label>
                  <input
                    type="text"
                    id="x"
                    value={currentMember.x}
                    onChange={(e) => setCurrentMember(prev => ({ ...prev, x: e.target.value }))}
                    placeholder="@cathy"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="facebook">Facebook</label>
                  <input
                    type="text"
                    id="facebook"
                    value={currentMember.facebook}
                    onChange={(e) => setCurrentMember(prev => ({ ...prev, facebook: e.target.value }))}
                    placeholder="cathy"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="threads">Threads</label>
                  <input
                    type="text"
                    id="threads"
                    value={currentMember.threads}
                    onChange={(e) => setCurrentMember(prev => ({ ...prev, threads: e.target.value }))}
                    placeholder="@cathy"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tiktok">TikTok</label>
                  <input
                    type="text"
                    id="tiktok"
                    value={currentMember.tiktok}
                    onChange={(e) => setCurrentMember(prev => ({ ...prev, tiktok: e.target.value }))}
                    placeholder="@cathy"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="mastodon">Mastodon</label>
                  <input
                    type="text"
                    id="mastodon"
                    value={currentMember.mastodon}
                    onChange={(e) => setCurrentMember(prev => ({ ...prev, mastodon: e.target.value }))}
                    placeholder="@cathy@mastodon.social"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="linkedin">LinkedIn</label>
                  <input
                    type="text"
                    id="linkedin"
                    value={currentMember.linkedin}
                    onChange={(e) => setCurrentMember(prev => ({ ...prev, linkedin: e.target.value }))}
                    placeholder="cathy"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bluesky">Bluesky</label>
                  <input
                    type="text"
                    id="bluesky"
                    value={currentMember.bluesky}
                    onChange={(e) => setCurrentMember(prev => ({ ...prev, bluesky: e.target.value }))}
                    placeholder="@cathy"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="youtube">YouTube</label>
                  <input
                    type="text"
                    id="youtube"
                    value={currentMember.youtube}
                    onChange={(e) => setCurrentMember(prev => ({ ...prev, youtube: e.target.value }))}
                    placeholder="@cathy"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="instagram">Instagram</label>
                  <input
                    type="text"
                    id="instagram"
                    value={currentMember.instagram}
                    onChange={(e) => setCurrentMember(prev => ({ ...prev, instagram: e.target.value }))}
                    placeholder="@cathy"
                  />
                </div>
              </div>
            </div>

            <button 
              type="button" 
              onClick={addStaffMember}
              className="add-button"
            >
              Save (and optionally add another)
            </button>
          </div>
        </div>

        {staffMembers.length > 0 && (
          <div className="staff-section">
            <div className="staff-header">
              <h2>Staff Members ({staffMembers.length})</h2>
              <button 
                onClick={exportJSON}
                className="export-button"
              >
                Export ZIP
              </button>
            </div>
            
            <div className="staff-list">
              {staffMembers.map((member, index) => (
                <div key={index} className="staff-card">
                  <div className="staff-info">
                    {member.photoPreview && (
                      <div className="staff-photo">
                        <img src={member.photoPreview} alt={member.name} />
                      </div>
                    )}
                    <h3>{member.name}</h3>
                    <p className="email">{member.email}</p>
                    <p className="role">{member.roles[0]}</p>
                    {member.bio && <p className="bio">"{member.bio}"</p>}
                    
                    {/* Social Media Links */}
                    <div className="social-links">
                      {member.twitter && (
                        <a href={member.twitter} target="_blank" rel="noopener noreferrer" className="social-link">
                          X
                        </a>
                      )}
                      {member.facebook && (
                        <a href={member.facebook} target="_blank" rel="noopener noreferrer" className="social-link">
                          Facebook
                        </a>
                      )}
                      {member.threads && (
                        <a href={member.threads} target="_blank" rel="noopener noreferrer" className="social-link">
                          Threads
                        </a>
                      )}
                      {member.tiktok && (
                        <a href={member.tiktok} target="_blank" rel="noopener noreferrer" className="social-link">
                          TikTok
                        </a>
                      )}
                      {member.mastodon && (
                        <a href={member.mastodon} target="_blank" rel="noopener noreferrer" className="social-link">
                          Mastodon
                        </a>
                      )}
                      {member.linkedin && (
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="social-link">
                          LinkedIn
                        </a>
                      )}
                      {member.bluesky && (
                        <a href={member.bluesky} target="_blank" rel="noopener noreferrer" className="social-link">
                          Bluesky
                        </a>
                      )}
                      {member.youtube && (
                        <a href={member.youtube} target="_blank" rel="noopener noreferrer" className="social-link">
                          YouTube
                        </a>
                      )}
                      {member.instagram && (
                        <a href={member.instagram} target="_blank" rel="noopener noreferrer" className="social-link">
                          Instagram
                        </a>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => removeStaffMember(index)}
                    className="remove-button"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Ghost Tip Section */}
      {BRANDING && (
        <div className="ghost-tip-section">
          <a 
            href="https://www.spectralwebservices.com/tipjar/" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="ghost-tip-link" 
            title="leave a tip?"
          >
            <img 
              alt="Ghost with mug" 
              className="ghost-tip-image" 
              src="https://www.spectralwebservices.com/content/images/size/w400/format/webp/2024/02/ghost-with-mug.svg"
            />
          </a>
        </div>
      )}
    </div>
  );
}

export default App;

